import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { UploadSimple, File, DownloadSimple, Trash, Eye, X, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const DocumentUploadSection = ({ category, relatedId, title = "Evidence Documents", viewOnly = false, showProjectSelector = false, projects = [] }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(relatedId || '');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, [relatedId, selectedProject]);

  const loadDocuments = async () => {
    try {
      const params = new URLSearchParams({ category });
      const projectId = relatedId || selectedProject;
      if (projectId) params.append('related_id', projectId);
      const response = await api.get(`/documents?${params}`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const projectId = relatedId || selectedProject;
    if (showProjectSelector && !projectId) {
      toast.error('Please select a project first');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const params = new URLSearchParams({ category });
        if (projectId) params.append('related_id', projectId);

        await api.post(`/documents/upload?${params}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} document(s) uploaded successfully`);
      loadDocuments();
    }
    if (failCount > 0) {
      toast.error(`${failCount} document(s) failed to upload`);
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download failed');
    }
  };

  const handlePreview = async (doc) => {
    if (!doc.content_type?.startsWith('image/')) {
      toast.error('Preview only available for images');
      return;
    }
    
    setPreviewDoc(doc);
    setPreviewLoading(true);
    
    try {
      const response = await api.get(`/documents/${doc.id}/preview`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPreviewUrl(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted successfully');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const isImage = (doc) => doc.content_type?.startsWith('image/');

  return (
    <div className="bg-white border border-gray-200 rounded-sm">
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">{title}</h3>
          {!viewOnly && (
            <label className="flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-sm text-sm hover:bg-gray-800 transition-colors cursor-pointer">
              <UploadSimple size={16} weight="bold" />
              {uploading ? 'Uploading...' : 'Upload Files'}
              <input
                type="file"
                data-testid={`upload-${category}-docs`}
                multiple
                onChange={handleFileSelect}
                disabled={uploading}
                className="hidden"
              />
            </label>
          )}
          {viewOnly && (
            <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded-sm">View Only - Preview Available</span>
          )}
        </div>
        
        {/* Project Selector */}
        {showProjectSelector && projects.length > 0 && (
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Select Project for Upload</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-sm text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            >
              <option value="">-- Select Project --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.project_name} - {p.customer}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="p-4">
        {loading ? (
          <p className="text-sm text-gray-500 text-center py-4">Loading documents...</p>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No documents uploaded yet</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc, idx) => (
              <div
                key={doc.id}
                data-testid={`document-item-${idx}`}
                className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-sm hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {isImage(doc) ? (
                    <ImageIcon size={20} className="text-green-600 flex-shrink-0" />
                  ) : (
                    <File size={20} className="text-gray-600 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.original_filename}</p>
                    <p className="text-xs text-gray-500 mono">
                      {formatFileSize(doc.size)} • {new Date(doc.created_at).toLocaleDateString()}
                      {isImage(doc) && <span className="ml-2 text-green-600">Image</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {/* Preview button - available for images even in view-only mode */}
                  {isImage(doc) && (
                    <button
                      data-testid={`preview-doc-${idx}`}
                      onClick={() => handlePreview(doc)}
                      className="flex items-center gap-1 px-3 py-1 text-green-600 hover:text-green-700 text-sm font-medium"
                      title="Preview Image"
                    >
                      <Eye size={16} /> Preview
                    </button>
                  )}
                  {!viewOnly && (
                    <>
                      <button
                        data-testid={`download-doc-${idx}`}
                        onClick={() => handleDownload(doc)}
                        className="flex items-center gap-1 px-3 py-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        <DownloadSimple size={16} /> Download
                      </button>
                      <button
                        data-testid={`delete-doc-${idx}`}
                        onClick={() => handleDelete(doc.id)}
                        className="flex items-center gap-1 px-3 py-1 text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        <Trash size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={closePreview}>
          <div className="bg-white rounded-sm max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-900">{previewDoc.original_filename}</h3>
                <p className="text-xs text-gray-500">Preview Only - Download Not Available</p>
              </div>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-gray-200 rounded-sm transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-100 min-h-[300px]">
              {previewLoading ? (
                <p className="text-gray-500">Loading preview...</p>
              ) : previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt={previewDoc.original_filename}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : (
                <p className="text-gray-500">Preview not available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
